import React, { useMemo } from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormControlLabel,
    Grid,
    Card,
    CardContent,
    IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArchiveIcon from '@mui/icons-material/Archive';
import CloseIcon from '@mui/icons-material/Close';
import { useDropzone } from 'react-dropzone';

/* ─────────────── 1. Upload-Zelle pro Sprache ─────────────── */
function UploadCell({ product, language, file, onFileChange }) {
    const onDrop = (accepted) => {
        if (accepted && accepted.length) onFileChange(product, language, accepted[0]);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: { 'application/zip': ['.zip'] },
        maxFiles: 1,
        onDrop,
    });

    return (
        <Card
            variant="outlined"
            sx={{
                borderStyle: 'dashed',
                backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            }}
        >
            <CardContent
                {...getRootProps()}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}
            >
                <input {...getInputProps()} />
                {file ? (
                    <>
                        <ArchiveIcon sx={{ mr: 1 }} />
                        <Typography noWrap sx={{ maxWidth: 140 }}>{file.name}</Typography>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onFileChange(product, language, null);
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </>
                ) : (
                    <Box textAlign="center">
                        <CloudUploadIcon fontSize="medium" />
                        <Typography variant="caption">{language.toUpperCase()}</Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

/* ─────────────── 2. Haupt-Sektion ─────────────── */
export default function ManageReleaseFilesSection({
    products = [],          // ausgewählte Produkte aus dem Dialog
    languages = [],         // ausgewählte Sprachen
    productMeta = {},       // { supervisor: { title, desc }, ... }  (frei definierbar)
    value = {},             // aktueller State   { supervisor: { overwrite, reuploadImages, uploads: { en:File } } }
    onChange = () => { },    // callback(nextState)
}) {
    /* Hilfs-Handler, um Teil-State zu ändern */
    const updateProductState = (product, patch) => {
        onChange({
            ...value,
            [product]: { ...value[product], ...patch },
        });
    };

    /* Alle relevanten Kombis errechnen */
    const combosPerProduct = useMemo(
        () =>
            products.reduce((acc, p) => {
                acc[p] = languages.map((l) => ({ product: p, language: l }));
                return acc;
            }, {}),
        [products, languages],
    );

    if (!products.length) return null;

    return (
        <Box mt={2} mr={3} ml={3}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Manage Release Files
            </Typography>

            {products.map((product) => {
                const prodState = value[product] || {
                    overwrite: false,
                    reuploadImages: false,
                    uploads: {},
                };
                const { title = product, desc = '' } = productMeta[product] || {};

                return (
                    <Accordion
                        key={product}
                        expanded={prodState.overwrite}
                        disableGutters
                        elevation={0}
                        sx={{ mb: 1, border: '1px solid', borderColor: 'divider' }}
                    >
                        {/* ───── Kopfzeile ───── */}
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box flexGrow={1}>
                                <Typography sx={{ fontWeight: 500 }}>{title}</Typography>
                                {desc && (
                                    <Typography variant="body2" color="text.secondary">
                                        {desc}
                                    </Typography>
                                )}
                            </Box>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={prodState.overwrite}
                                        onChange={(e) =>
                                            updateProductState(product, { overwrite: e.target.checked })
                                        }
                                    />
                                }
                                label="(Re) Upload Files"
                            />
                        </AccordionSummary>

                        {/* ───── Details - nur wenn overwrite = true ───── */}
                        <AccordionDetails>

                            <Grid
                                container
                                spacing={1.5}
                                sx={{ mt: 1 }}
                            >
                                {combosPerProduct[product].map(({ product: p, language: l }) => {
                                    const file = prodState.uploads?.[l] || null;
                                    return (
                                        <Grid item xs={6} sm={4} md={3} key={`${p}_${l}`}>
                                            <UploadCell
                                                product={p}
                                                language={l}
                                                file={file}
                                                onFileChange={(prod, lang, fileObj) => {
                                                    const nextUploads = { ...prodState.uploads, [lang]: fileObj };
                                                    updateProductState(prod, { uploads: nextUploads });
                                                }}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>

                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
}
